import { Accordion, Alert, Button, ButtonGroup, Modal } from 'react-bootstrap';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle } from 'react-icons/fa';
import AiVehicleResults from '../AiVehicleResults';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import i18next from 'i18next';
import Select from 'react-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAiContext } from '../../../context/AiContext';
import { NOLEGGIATORI_QUOTATORE, STELLANTIS_MARCHE } from '../../../utils/constants';
import _ from 'lodash';
import toaster from '../../../utils/toaster';
import { quickoGtmQuotationData } from '../../../utils/quicko';
import { useApiClient } from '../../../hooks/useApiClient';
import QuickoServicesModal from './quickoServicesModal';
import './quickoModal.scss';
import { ApiError, RoleEnum } from 'src/api';
import { QuickoQuotationNoleggiatoriDTO, VehicleQuickoQuotationRequestDTO } from 'src/api/models/VehicleQuickoQuotationRequestDTO';
import { useQuickoQuotationParams } from 'src/hooks/useQuickoQuotationParams';
import { useAuth } from 'src/hooks/useAuth';
import { IoWarningOutline } from 'react-icons/io5';
import {
	QUOTATION_BUCKETS,
	QuotationBucket,
	QuotationRowDiff,
	diffQuotations,
	formatDiffValue,
} from 'src/utils/quotationDiff';

interface UserSelection {
	rentalDuration: number;
	totalKm: number;
	advancePayment: number;
	commissionType: string;
	commissionValue: number;
}

const advancePaymentOptions = [
	{ value: 0, label: '0' },
	{ value: 3000, label: '3000' },
	{ value: 5000, label: '5000' },
	{ value: 7000, label: '7000' },
	{ value: 9000, label: '9000' },
];

const buildValidationSchema = ({ maxDownPayment }) => Yup.object({
	rentalDuration: Yup.number()
		.moreThan(0, i18next.t('insert_valid_duration'))
		.required(i18next.t('insert_valid_duration')),

	totalKm: Yup.number()
		.moreThan(0, i18next.t('insert_valid_number_km'))
		.required(i18next.t('km_required')),

	advancePayment: Yup.number()
		.lessThan(maxDownPayment, i18next.t('max_down_payment_exceedeed', { maxValue: '20%' }))
		.required(i18next.t('downpayment_required')),
});

interface QuickoDebugHeaderProps {
	title: string;
	endpoint: string;
	jatoId?: number | string | null;
	vehicleCode?: number | string | null;
	usedVehicleId?: number | string | null;
}

/** Intestazione di debug di un set di quotazioni: sorgente e id inviato. */
const QuickoDebugHeader = ({
	title,
	endpoint,
	jatoId,
	vehicleCode,
	usedVehicleId,
}: QuickoDebugHeaderProps) => (
	<div className="quicko-debug">
		<div className="quicko-debug__source">
			<span className="quicko-debug__title">{title}</span>
			<code className="quicko-debug__endpoint">{endpoint}</code>
		</div>
		<div className="quicko-debug__chips">
			<span className="quicko-debug__chip">
				<span className="quicko-debug__key">jato_id</span>
				<span className="quicko-debug__value">{jatoId ?? '—'}</span>
			</span>
			<span className="quicko-debug__chip">
				<span className="quicko-debug__key">vehicle_code</span>
				<span className="quicko-debug__value">{vehicleCode ?? '—'}</span>
			</span>
			<span className="quicko-debug__chip quicko-debug__chip--used">
				<span className="quicko-debug__key">inviato</span>
				<span className="quicko-debug__value">{usedVehicleId ?? '—'}</span>
			</span>
		</div>
	</div>
);

interface QuickoDiffAlertProps {
	diffs: QuotationRowDiff[];
	legacyError?: string | null;
	platformError?: string | null;
	canCompare: boolean;
}

const BUCKET_LABELS: Record<string, string> = {
	best_offers: 'Best offers',
	ai_offers: 'Stime Quicko',
	similar_offers: 'Offerte simili',
};

/** Confronto legacy disattivato. */
const SHOW_LEGACY_COMPARISON: boolean = false;

/** Bucket temporaneamente esclusi dal confronto legacy/V2. */
const HIDDEN_BUCKETS: QuotationBucket[] = ['ai_offers', 'similar_offers'];

/** Riepilogo delle righe che legacy e V2 non producono allo stesso modo. */
const QuickoDiffAlert = ({
	diffs,
	legacyError,
	platformError,
	canCompare,
}: QuickoDiffAlertProps) => {
	const grouped = useMemo(() => {
		const byBucket = _.groupBy(diffs, 'bucket');

		return QUOTATION_BUCKETS.filter(
			(bucket) =>
				!HIDDEN_BUCKETS.includes(bucket) && byBucket[bucket]?.length
		).map((bucket) => ({ bucket, rows: byBucket[bucket] }));
	}, [diffs]);
	const visibleCount = _.sumBy(grouped, (g) => g.rows.length);
	const [activeBucket, setActiveBucket] = useState<QuotationBucket | null>(null);

	// Il bucket selezionato può sparire quando arriva un nuovo confronto.
	useEffect(() => {
		setActiveBucket((prev) =>
			prev && grouped.some((g) => g.bucket === prev)
				? prev
				: grouped[0]?.bucket ?? null
		);
	}, [grouped]);

	const activeDiffs = grouped.find((g) => g.bucket === activeBucket)?.rows ?? [];

	if (legacyError || platformError) {
		const failed = [
			legacyError ? 'legacy' : null,
			platformError ? 'V2' : null,
		].filter(Boolean);

		return (
			<Alert variant="secondary" className="mt-4 mb-0 py-2">
				<div className="d-flex align-items-center gap-2">
					<IoWarningOutline />
					<small className="ms-2">
						Non è possibile confrontare legacy e V2: la chiamata {failed.join(' e ')}{' '}
						{failed.length > 1 ? 'sono fallite' : 'è fallita'}.
					</small>
				</div>
			</Alert>
		);
	}

	if (!canCompare) {
		return null;
	}

	if (!visibleCount) {
		return (
			<Alert variant="success" className="mt-4 mb-0 py-2">
				<div className="d-flex align-items-center gap-2">
					<FaCheckCircle />
					<small className="ms-2">Legacy e V2 coincidono su tutte le righe.</small>
				</div>
			</Alert>
		);
	}

	return (
		<Alert variant="warning" className="mt-4 mb-0">
			<Alert.Heading className="d-flex align-items-center gap-2" style={{ fontSize: 16 }}>
				<IoWarningOutline />
				<span className="ms-2">
					{visibleCount} {visibleCount === 1 ? 'riga divergente' : 'righe divergenti'}{' '}
					fra legacy e V2
				</span>
			</Alert.Heading>
			<ButtonGroup size="sm" className="quicko-diff-buckets">
				{grouped.map(({ bucket, rows }) => (
					<Button
						key={bucket}
						variant={bucket === activeBucket ? 'warning' : 'outline-warning'}
						onClick={() => setActiveBucket(bucket)}
					>
						{BUCKET_LABELS[bucket] ?? bucket}
						<span className="quicko-diff-buckets__count">{rows.length}</span>
					</Button>
				))}
			</ButtonGroup>
			<ul className="quicko-diff-list">
				{activeDiffs.map((diff) => (
					<li key={`${diff.bucket}-${diff.rowKey}`}>
						<span className="quicko-diff-list__noleggiatore">{diff.noleggiatore}</span>
						{diff.kind === 'only_legacy' && <span>solo in legacy</span>}
						{diff.kind === 'only_v2' && <span>solo in V2</span>}
						{diff.kind === 'values' &&
							diff.fields.map((f) => (
								<span key={f.field} className="quicko-diff-list__field">
									{f.label}: <code>{formatDiffValue(f.legacy)}</code> →{' '}
									<code>{formatDiffValue(f.v2)}</code>
									{f.delta !== null && f.delta !== undefined && (
										<em className="ms-1">
											({f.delta > 0 ? '+' : ''}
											{formatDiffValue(f.delta)})
										</em>
									)}
								</span>
							))}
					</li>
				))}
			</ul>
		</Alert>
	);
};

interface QuickoResultsPanelProps {
	quotations: any;
	showModal: any;
	showLimit: number;
	modello?: string;
}

/**
 * Rende le tabelle di un singolo set di quotazioni. Estratto per poter mostrare
 * in parallelo le risposte di quicko-quotation (legacy) e platform/quotation.
 */
const QuickoResultsPanel = ({
	quotations,
	showModal,
	showLimit,
	modello,
}: QuickoResultsPanelProps) => {
	const { t } = useTranslation();
	const [accordionActiveKey, setAccordionActiveKey] = useState<any>(null);

	useEffect(() => {
		if (quotations?.best_offers?.length < showLimit) {
			setAccordionActiveKey(quotations?.similar_offers?.length ? '0' : '1');
		}
	}, [quotations, showLimit]);

	if (_.isEmpty(quotations)) {
		return null;
	}

	const hasBestOffers = quotations['best_offers']?.length >= showLimit;

	return (
		<>
			{hasBestOffers ? (
				<div className="mt-4">
					<div className="fw-bold d-flex text-success">
						<FaCheckCircle className="fs-5" />
						<span className="my-auto ms-3">{t('best_offers')}</span>
					</div>
					<div className="mt-2">
						<AiVehicleResults
							showModal={showModal}
							type="best_offers"
							quotationsOverride={quotations}
						/>
					</div>
				</div>
			) : (
				''
			)}

			<div className="mt-4">
				<Accordion
					activeKey={accordionActiveKey}
					onSelect={(e) => setAccordionActiveKey(e)}
				>
					{quotations['similar_offers']?.length && !hasBestOffers ? (
						<Accordion.Item eventKey="0">
							<Accordion.Header>
								<div className="fw-bold text-cyan">
									{t('similar_offers')} - {modello}
								</div>
							</Accordion.Header>
							<Accordion.Body className="p-0">
								<AiVehicleResults
									showModal={showModal}
									type="similar_offers"
									quotationsOverride={quotations}
								/>
							</Accordion.Body>
						</Accordion.Item>
					) : (
						''
					)}
					{quotations['best_offers']?.length < showLimit ? (
						<Accordion.Item eventKey="1">
							<Accordion.Header>
								<img
									src="/svg-icons/quicko-logo-q.png"
									style={{ width: '25px' }}
									alt="Quicko"
								></img>
								<span className="ms-3 fw-bold text-cyan">
									{t('estimated_forecasts_of')} QUICKO
								</span>
							</Accordion.Header>
							<Accordion.Body className="p-0">
								<Row>
									<Col lg={12}>
										<AiVehicleResults
											showModal={showModal}
											type="ai_offers"
											quotationsOverride={quotations}
										/>
									</Col>
								</Row>
							</Accordion.Body>
						</Accordion.Item>
					) : (
						''
					)}
				</Accordion>
			</div>
		</>
	);
};

const QuickoModal = ({ showModal, isVisible, closeFn, onConfirmEvent = _.noop }) => {
	// const initialValues = { groupName: '' };
	const { t } = useTranslation();
	const { userRole } = useAuth();
	const {
		setShowResults,
		setQuotReqState,
		quotReqState,
		quotations,
		setQuotations,
		services,
		quickoHelper,
		fadWarning,
		setFadWarning
	} = useAiContext();
	const [isServiceModalVisible, setIsServiceModalVisible] = useState(false);
	const { apiClient, handleApi } = useApiClient();
	const {
		optionalTotals,
		vehicleState: { dati_veicolo },
	} = quotReqState;
	const [userSelection, setUserSelection] = useState<UserSelection>(
		{} as UserSelection
	);
	const [loading, setLoading] = useState(false);
	const formikRef = useRef<any>(null);
	// Confronto temporaneo fra quicko-quotation (legacy, in AiContext) e platform/quotation.
	const [platformQuotations, setPlatformQuotations] = useState<any>(null);
	const [platformError, setPlatformError] = useState<string | null>(null);
	const [legacyError, setLegacyError] = useState<string | null>(null);
	const [legacySnapshot, setLegacySnapshot] = useState<any>(null);
	const [platformSnapshot, setPlatformSnapshot] = useState<any>(null);
	const [legacyVehicleId, setLegacyVehicleId] = useState<number | string>();
	const [platformVehicleId, setPlatformVehicleId] = useState<number | string>();
	const { isLoadingParams, quotationParams } = useQuickoQuotationParams(dati_veicolo?.vehicle_code || dati_veicolo?.jato_id, dati_veicolo?.marca, isVisible);
	const isSegnalatore = userRole === RoleEnum.SEGNALATORE;
	const quotationDiffs = useMemo(
		() => diffQuotations(legacySnapshot, platformSnapshot),
		[legacySnapshot, platformSnapshot]
	);
	// Il confronto si mostra solo quando entrambe le chiamate hanno risposto.
	const canCompare = !_.isEmpty(legacySnapshot) && !_.isEmpty(platformSnapshot);

	const closeServiceModal = () => {
		setIsServiceModalVisible(false);
		formikRef.current?.submitForm();
	};

	const getServicesString = () => {
		let result: string[] = [];

		for (let k in services) {
			if (Array.isArray(services[k])) {
				services[k].forEach((s) => {
					if (!s.isChecked) {
						return;
					}

					result.push(s.labelRiepilogo);
				});
			} else {
				if (services[k].isChecked) {
					result.push(services[k].labelRiepilogo);
				}
			}
		}

		return result.join(', ');
	};

	const handleSubmit = async (values,) => {
		setUserSelection(values);
		setQuotations(null);
		setFadWarning(null);
		const {
			rentalDuration,
			totalKm,
			advancePayment,
			commissionValue,
			commissionType,
		} = values;
		setQuotReqState((prev) => ({
			...prev,
			rentalDuration,
			totalKm,
			advancePayment,
			commissionValue,
			commissionType,
		}));

		const rca_penale_risarcitoria =
			services.assicurazioneRC.find((option) => option.isChecked)?.value || 0;
		const incendio_furto_scoperto_perc =
			services.furtoIncendio.find((option) => option.isChecked)?.value || 0;
		const kasko_franchigia =
			services.assicurazioneKasko.find((option) => option.isChecked)?.value ||
			0;
		// const copertura_pai_YN = services.assicurazionePAI.isChecked ? 1 : 0;
		const tutela_legale_YN = services.tutelaLegale.isChecked ? 1 : 0;
		let auto_sostitutiva_gruppo =
			services.vetturaSostitutiva.find((option) => option.isChecked)?.value ||
			0;

		if (auto_sostitutiva_gruppo === 'citycar') {
			auto_sostitutiva_gruppo = 'city car';
		}

		const checkedGommeService = services.gomme.find(
			(option) => option.isChecked === true
		);

		const tipo_gomma = checkedGommeService
			? checkedGommeService.value
			: 'Assente';
		let gomme_illimitate_YN = checkedGommeService
			? checkedGommeService.unlimited
			: false;
		let n_treno_gomme_estive = checkedGommeService
			? checkedGommeService.data?.estive || 0
			: 0;
		let n_treno_gomme_invernali = checkedGommeService
			? checkedGommeService.data?.invernali || 0
			: 0;

		let noleggiatori: QuickoQuotationNoleggiatoriDTO[] = [];
		console.log("noleggiatori_quotatore", quotReqState.vehicleState.lista_noleggiatori_quotatore)

		let lista_noleggiatori_quotatore =
			quotReqState.vehicleState.lista_noleggiatori_quotatore || [];
		lista_noleggiatori_quotatore = lista_noleggiatori_quotatore.filter(
			(el) => el.is_checked
		);
		lista_noleggiatori_quotatore.forEach((el) => {
			let nquot = Object.values(NOLEGGIATORI_QUOTATORE).find(
				(n) => [n.name, n.code].includes(el.noleggiatore)
			);
			if (nquot) {
				noleggiatori.push(
					...nquot.noleggiatori_quicko
						.map((n: string) => n.toLowerCase())
						.map((n: string) => ({
							noleggiatore: n,
							extra_sconto_perc: el.extra_sconto_perc as number || 0
						}))
				);
			}
		});


		const payload: VehicleQuickoQuotationRequestDTO = {
			rca_YN: +services.assicurazioneRC.some((o) => o.isChecked), // rca_penale_risarcitoria === 0 ? 0 : 1,
			incendio_furto_YN: +services.furtoIncendio.some((o) => o.isChecked), // incendio_furto_scoperto_perc === 0 ? 0 : 1,
			kasko_YN: +services.assicurazioneKasko.some((o) => o.isChecked), // kasko_franchigia === 0 ? 0 : 1,
			allestimento: dati_veicolo.allestimento,
			anticipo_no_iva: advancePayment,
			marca: dati_veicolo.marca,
			modello: dati_veicolo.modello,
			prezzo_accessori_no_iva: optionalTotals,
			durata: rentalDuration,
			prezzo_listino_no_iva: dati_veicolo.prezzo_listino,
			incendio_furto_scoperto_perc,
			kasko_franchigia,
			km_annui: totalKm,
			rca_penale_risarcitoria,
			tipo_gomma,
			tutela_legale_YN,
			gomme_illimitate_YN,
			n_treno_gomme_estive,
			n_treno_gomme_invernali,
			// copertura_pai_YN,
			auto_sostitutiva_gruppo,
			noleggiatori: _.uniq(noleggiatori),
			carburante_principale_generico: dati_veicolo.alimentazione,
			numero_porte: dati_veicolo.porte,
			numero_posti: dati_veicolo.posti,
			potenza_cv: dati_veicolo.cavalli,
			tipo_cambio: dati_veicolo.cambio,
			tipo_carrozzeria: dati_veicolo.carrozzeria,
			vehicle_id: dati_veicolo.jato_id,
			extra_sconto_perc: dati_veicolo.extra_sconto_perc,
		};

		if (commissionValue && !quickoHelper.isZero(commissionValue)) {
			Object.assign(payload, {
				commission_value: quickoHelper.isSegnalatore
					? quickoHelper.computeSegnalatoreCommissions(commissionValue)
					: commissionValue,
				commission_type: commissionType,
			});
		}

		// const additionRules = (quotation) => {
		// 	switch (auto_sostitutiva_gruppo) {
		// 		case 'city car':
		// 			quotation.canone += 10;
		// 			break;
		// 		case 'media':
		// 			quotation.canone += 15;
		// 			break;
		// 		case 'superior':
		// 			quotation.canone += 23;
		// 			break;
		// 		default:
		// 			break;
		// 	}

		// 	if (incendio_furto_scoperto_perc === 0) {
		// 		quotation.canone += 15;
		// 	}

		// 	if (copertura_pai_YN) {
		// 		quotation.canone += 1;
		// 	}

		// 	if (!gomme_illimitate_YN) {
		// 		const totalGomme = n_treno_gomme_estive + n_treno_gomme_invernali;

		// 		switch (tipo_gomma) {
		// 			case 'quality':
		// 				quotation.canone += 15 * totalGomme;
		// 				break;
		// 			case 'premium':
		// 				quotation.canone += 20 * totalGomme;
		// 				break;
		// 			default:
		// 				break;
		// 		}
		// 	}
		// 	return quotation;
		// };

		onConfirmEvent(quickoGtmQuotationData(payload, services))


		// Confronto: stesso payload, ma il nuovo endpoint identifica il veicolo con
		// vehicle_code (jato_id come fallback) e non accetta vehicle_id.
		const platformPayload: VehicleQuickoQuotationRequestDTO = {
			..._.omit(payload, 'vehicle_id'),
			vehicle_code: dati_veicolo.vehicle_code ?? dati_veicolo.jato_id,
		};
		setLegacyVehicleId(payload.vehicle_id);
		setPlatformVehicleId(platformPayload.vehicle_code);
		setPlatformQuotations(null);
		setPlatformError(null);
		setLegacyError(null);
		setLegacySnapshot(null);
		setPlatformSnapshot(null);

		apiClient.motornetControllerService
			.createQuotation(platformPayload)
			.then((platformResponse) => {
				const platformQuotations = quickoHelper.updateQuotationCommissions(
					platformResponse,
					commissionValue
				);

				if (SHOW_LEGACY_COMPARISON) {
					setPlatformSnapshot(_.cloneDeep(platformQuotations));
					setPlatformQuotations(platformQuotations);
				} else {
					setQuotations(platformQuotations);
				}

				setShowResults(true);
			})
			.catch((error: Error) => {
				console.error('Error querying platform/quotation:', error);
				const text = error?.message || i18next.t('inference_not_possible');
				setPlatformError(text);

				if (!SHOW_LEGACY_COMPARISON) {
					toaster.error(text);
				}
			});

		if (!SHOW_LEGACY_COMPARISON) {
			return;
		}

		handleApi(apiClient.vehicleController.quickoQuotation(payload))
			.then((responseQuotations) => {
				setLoading(true);

				// const updatedQuotations = responseQuotations
				// 	.map((quotation, index) => {
				// 		let discountedCanone = 0;
				// 		const originalCanone = quotation.canone;

				// 		const fullPriceVehicle =
				// 			optionalTotals + dati_veicolo.prezzo_listin;
				// 		setFullPriceVehicle(fullPriceVehicle);
				// 		const existDiscount =
				// 			dati_veicolo.extra_sconto_perc !== 0 &&
				// 			dati_veicolo.extra_sconto_perc != null &&
				// 			!isNaN(dati_veicolo.extra_sconto_perc);
				// 		setExistDiscount(existDiscount);

				// 		quotation = additionRules(quotation);

				// 		let discount = 0;
				// 		if (existDiscount) {
				// 			// Calc discount canone
				// 			// [(% * (prezzo listino + optional) ) / mesi ]*(-1).
				// 			discount =
				// 				((dati_veicolo.extra_sconto_perc / 100) *
				// 					(dati_veicolo.prezzo_listino + optionalTotals)) /
				// 				rentalDuration;
				// 			discountedCanone = quotation.canone - discount;
				// 		}

				// 		// const commObj = calcQuotation(quotation, rentalDuration, optionalTotals, dati_veicolo)
				// 		const updatedQuotation = {
				// 			...quotation,
				// 			discountedCanone: parseFloat(discountedCanone.toFixed(2)),
				// 			originalCanone: parseFloat(originalCanone.toFixed(2)),
				// 			canone: parseFloat(quotation.updatedCanone.toFixed(2)) - discount,
				// 			commissioni: parseFloat(quotation.commissioni.toFixed(2)),
				// 			typeInput: '€',
				// 			percentage: quotation.percentage,
				// 			fullPriceVehicle: optionalTotals + dati_veicolo.prezzo_listino,
				// 			optionalTotals,
				// 			prezzo_listino: dati_veicolo.prezzo_listino,
				// 		};
				// 		return updatedQuotation;
				// 	})
				// 	.sort((a, b) => a.noleggiatore.localeCompare(b.noleggiatore));
				responseQuotations = quickoHelper.updateQuotationCommissions(responseQuotations, commissionValue);

				setLegacySnapshot(_.cloneDeep(responseQuotations));
				setQuotations(responseQuotations);
				// GTMsendEvent({
				// 	event: 'complete_quicko_quotes',
				// 	evtCat: 'account',
				// 	evtAct: 'open',
				// 	evtLab: 'open_opportunity'
				// })
				// sendVehicleGTMEvent('complete_quicko_quotes', response, {
				// 				...data,
				// 				supply: response.dati_veicolo.alimentazione,
				// 				colours: response.dettagli_veicolo.colore_interno,
				// 				chassis: response.dettagli_veicolo.telaio,
				// 				number_plate: response.dettagli_veicolo.targa ?? '',
				// 				body_car: response.dati_veicolo.carrozzeria,
				// 				notes: response.dettagli_veicolo.note ?? ''

				// 			})


				setShowResults(true);
				setLoading(false);
				// setClearInputs();
			})
			.catch((error: ApiError) => {
				setLoading(false);

				if (error?.body?.messageList?.length) {
					try {
						const message = JSON.parse(error?.body?.messageList[0]);
						const text = message?.message || i18next.t('inference_not_possible');
						setLegacyError(text);
						toaster.error(text);

						return;
					} catch (e) { }
				}

				console.error('Error querying the inference:', error);
				setLegacyError(error?.message || i18next.t('inference_not_possible'));
				toaster.error(i18next.t('inference_not_possible'));
			});
	};

	const CustomSelect = ({ options, field, form }) => (
		<Select
			menuPosition="fixed"
			options={options}
			name={field.name}
			isLoading={isLoadingParams}
			value={
				options ? options.find((option) => option.value === field.value) : ''
			}
			onChange={(option) => form.setFieldValue(field.name, option.value)}
			onBlur={field.onBlur}
		/>
	);

	/**
	 * Il massimale deve essere pari al 20% del valore del veicolo (prezzo di listino + optional) senza IVA.
	 * @param prezzoListino
	 * @param totalOptionals
	 * @returns
	 */
	const computeMaxDownPayment = (prezzoListino: number = 0, totalOptionalsNoIva: number = 0) => {
		return (prezzoListino + totalOptionalsNoIva) * 0.2
	}

	return (
		<>
			<Modal size="xl" show={isVisible}
				onHide={() => {
					setFadWarning(null);
					setPlatformQuotations(null);
					setPlatformError(null);
					setLegacyError(null);
					setLegacySnapshot(null);
					setPlatformSnapshot(null);
					closeFn?.()
				}}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						Quicko{' '}
						<span className="text-muted">
							- {dati_veicolo?.marca} {dati_veicolo?.modello}{' '}
							{dati_veicolo?.allestimento}
						</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="quicko-modal-body">
					<Formik
						innerRef={formikRef}
						initialValues={{
							rentalDuration: 0,
							totalKm: 0,
							advancePayment: 0,
							commissionValue: 0,
							commissionType: 'EURO',
						}}
						validationSchema={buildValidationSchema({
							maxDownPayment: computeMaxDownPayment(dati_veicolo?.prezzo_listino, optionalTotals)
						})}
						onSubmit={handleSubmit}
					>
						{(formik) => (
							<Form className="p-0 pb-2" onSubmit={e => {
								e.preventDefault();
								formik.handleSubmit(e);
							}}>
								<Row>
									<Col lg="3" className="d-flex">
										<h3 className="text-cyan mt-4">{t('rental_plan')}</h3>
									</Col>
									<Col lg={3} className="pr-4">
										<div>
											<label htmlFor="rentalDuration">
												{t('rental_duration')}
											</label>
											<Field
												component={CustomSelect}
												name="rentalDuration"
												options={quotationParams.rentalDurationOptions}
											/>
											{formik.touched.rentalDuration &&
												formik.errors.rentalDuration ? (
												<div style={{ color: 'red' }}>
													{formik.errors.rentalDuration}
												</div>
											) : null}
										</div>
									</Col>
									<Col lg={3} className="pr-4">
										<div>
											<label htmlFor="totalKm">{t('annual_km')}</label>
											<Field
												component={CustomSelect}
												name="totalKm"
												options={quotationParams.totalKmOptions}
											/>
											{formik.touched.totalKm && formik.errors.totalKm ? (
												<div style={{ color: 'red' }}>
													{formik.errors.totalKm}
												</div>
											) : null}
										</div>
									</Col>

									<Col lg={3} className="pr-4">
										<div>
											<label htmlFor="advancePayment">
												{t('downpayment')}
											</label>
											<Field
												type="number"
												step="any"
												name="advancePayment"
												className="form-control"
												placeholder={t('enter_downpayment')}
												style={{ backgroundColor: '#ffffff' }}

											/>
											{formik.touched.advancePayment &&
												formik.errors.advancePayment ? (
												<div style={{ color: 'red' }}>
													{formik.errors.advancePayment}
												</div>
											) : null}
										</div>
									</Col>
								</Row>

								<Row className="mt-3">
									<Col lg={3} className="d-flex">
										<div className="fw-bold text-capitalize text-cyan">
											{t('services')}
										</div>
									</Col>
									<Col lg={9}>
										<div>{getServicesString()}</div>
										<Button
											className="px-0"
											onClick={(e) => {
												setIsServiceModalVisible(true);
											}}
											variant="link"
										>
											{t('modify_add_services')}
										</Button>
									</Col>
								</Row>
								<Row className="mt-3">
									<Col lg="3" className="d-flex">
										<label className="my-auto fw-bold text-cyan">
											{isSegnalatore ? t('commissions') : t('change_commissions')}
										</label>
									</Col>
									<Col lg="auto">
										<ButtonGroup>
											<input
												onChange={(opt) => {
													formik.setFieldValue(
														'commissionValue',
														opt?.currentTarget.value
													);
												}}
												type="number"
												step="any"
												name="commissionValue"
												className="form-control"
												min="0"
											/>

											{!isSegnalatore && <select
													style={{ maxWidth: '4rem' }}
													className="form-select ms-1"
													name="commissionType"
													disabled={isSegnalatore}
													onChange={(opt) => {
														formik.setFieldValue(
															'commissionType',
															opt?.currentTarget.value
														);
													}}
												>
													<option value="EURO">€</option>
													<option value="PERCENTAGE">%</option>
												</select>}
										</ButtonGroup>
									</Col>
								</Row>

								<Row className="mt-4">
									<Col className="mx-auto" lg={3} xs={100}>
										<Button type="submit" className="btn-cyan w-100">
											{t('confirm')}
										</Button>
									</Col>
								</Row>
							</Form>
						)}
					</Formik>
					{!!fadWarning && <Alert variant="warning" className="mt-3 py-4" dismissible onClose={() => setFadWarning(null)}>
							<Alert.Heading className="d-flex align-items-center gap-2 text-center">
								<IoWarningOutline />
								<div className="ms-2" style={{fontSize: 16}}>
									{fadWarning}
								</div>
							</Alert.Heading>
					</Alert>}
					{/* Temporaneo: confronto fra i due endpoint di quotazione. */}
					{SHOW_LEGACY_COMPARISON ? (
						!_.isEmpty(quotations) ||
						!_.isEmpty(platformQuotations) ||
						platformError ? (
						<>
							<QuickoDiffAlert
							diffs={quotationDiffs}
							legacyError={legacyError}
							platformError={platformError}
							canCompare={canCompare}
						/>

							<div className="mt-4">
								<QuickoDebugHeader
									title="Legacy"
									endpoint="POST /vehicles/quicko-quotation"
									jatoId={dati_veicolo?.jato_id}
									vehicleCode={dati_veicolo?.vehicle_code}
									usedVehicleId={legacyVehicleId}
								/>
								<QuickoResultsPanel
									quotations={quotations}
									showModal={showModal}
									showLimit={quotationParams.showLimit}
									modello={dati_veicolo?.modello}
								/>
							</div>

							<div className="mt-5">
								<QuickoDebugHeader
									title="V2"
									endpoint="POST /platform/quotation"
									jatoId={dati_veicolo?.jato_id}
									vehicleCode={dati_veicolo?.vehicle_code}
									usedVehicleId={platformVehicleId}
								/>
								{platformError ? (
									<Alert variant="danger" className="mt-2 mb-0">
										{platformError}
									</Alert>
								) : (
									<QuickoResultsPanel
										quotations={platformQuotations}
										showModal={showModal}
										showLimit={quotationParams.showLimit}
										modello={dati_veicolo?.modello}
									/>
								)}
							</div>
						</>
						) : (
							''
						)
					) : platformError ? (
						<Alert variant="danger" className="mt-4 mb-0">
							{platformError}
						</Alert>
					) : (
						<QuickoResultsPanel
							quotations={quotations}
							showModal={showModal}
							showLimit={quotationParams.showLimit}
							modello={dati_veicolo?.modello}
						/>
					)}
				</Modal.Body>

			</Modal>
			<QuickoServicesModal
				closeFn={closeServiceModal}
				isVisible={isServiceModalVisible}
			/>
		</>
	);
};

export default QuickoModal;
